import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financialAccountApi } from '../../services/api/financialAccountApi';
import { financialStatementApi } from '../../services/api/financialStatementApi';
import { supabase } from '@/integrations/supabase/client';
import type { FinancialAccountWithBalances, AccountType, BalanceType } from '../../models/FinancialAccount';
import type { FinancialStatement } from '../../models/FinancialStatement';
import type { FinancialBatchFile } from '../../models/BatchPayout';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { downloadCSV } from '@/utils/csvExport';
import { downloadStatementPDF, downloadStatementCSV, downloadAllAccountsStatementPDF } from '@/utils/statementPdfUtils';

export const FinancialAccounts: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedRole, userRoles, hasPermission, getCurrentRoleIds } = useAuth();
  const [selectedAccountType, setSelectedAccountType] = useState<AccountType | ''>('');
  const [selectedAccount, setSelectedAccount] = useState<FinancialAccountWithBalances | null>(null);
  const [showReleaseFundsDialog, setShowReleaseFundsDialog] = useState(false);
  const [releaseAmount, setReleaseAmount] = useState<string>('');
  const [selectedBatchId, setSelectedBatchId] = useState<number | 'new' | null>(null);
  const [newBatchName, setNewBatchName] = useState<string>('');
  // Bulk release: which accounts are checked, the modal toggle, per-account
  // amounts (default to available, but editable), and progress while running.
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<number>>(new Set());
  const [showBulkReleaseDialog, setShowBulkReleaseDialog] = useState(false);
  const [bulkAmounts, setBulkAmounts] = useState<Record<number, string>>({});
  const [bulkResults, setBulkResults] = useState<{ done: number; total: number; errors: Array<{ name: string; error: string }> } | null>(null);
  const [isBulkReleasing, setIsBulkReleasing] = useState(false);
  const [showStatementExportDialog, setShowStatementExportDialog] = useState(false);
  const [selectedStatementId, setSelectedStatementId] = useState<number | null>(null);
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [exportAccountId, setExportAccountId] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [includeEmptyAccounts, setIncludeEmptyAccounts] = useState(true);
  const [includePlatform, setIncludePlatform] = useState(true);

  // Check if user is platform or has finance access
  const isPlatformUser = selectedRole?.type === 'platform';
  const hasFinanceAccess = hasPermission(getCurrentRoleIds(), 'write', 'financial_accounts');

  // Fetch accounts
  const { data: accounts, isLoading, error, refetch } = useQuery({
    queryKey: ['financialAccounts', selectedAccountType],
    queryFn: () =>
      financialAccountApi.getAccounts({
        account_type: selectedAccountType || undefined,
        include_balances: true,
      }),
  });

  // Fetch statements for export dialog
  const { data: statements } = useQuery({
    queryKey: ['financialStatements'],
    queryFn: () => financialStatementApi.getStatements(),
  });

  // Fetch available batches for release funds dialog
  const { data: availableBatches, refetch: refetchBatches } = useQuery({
    queryKey: ['availableBatches'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('financial_batch_files')
        .select('*')
        .in('status', ['draft', 'generated'])
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as FinancialBatchFile[];
    },
    enabled: showReleaseFundsDialog,
  });

  // Release funds mutation
  const releaseFundsMutation = useMutation({
    mutationFn: async ({ accountId, amount, batchId, newBatchName }: {
      accountId: number;
      amount: number;
      batchId?: number | null;
      newBatchName?: string;
    }) => {
      return financialAccountApi.releaseFunds(accountId, amount, {
        batch_id: batchId,
        new_batch_name: newBatchName,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['financialAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['availableBatches'] });
      setShowReleaseFundsDialog(false);
      setReleaseAmount('');
      setSelectedBatchId(null);
      setNewBatchName('');
      toast({
        title: 'Success',
        description: `Funds released successfully! Added to batch ${data.batch_number}`,
      });
      // Reload the selected account to show updated balances
      if (selectedAccount) {
        financialAccountApi.getAccount(selectedAccount.id).then(setSelectedAccount);
      }
    },
    onError: (error: any) => {
      console.error('Release funds error:', error);
      let errorMessage = `Failed to release funds: ${error?.message || String(error)}`;

      // If the error has response data with missing_fields, include that
      if (error?.response?.missing_fields && Array.isArray(error.response.missing_fields)) {
        errorMessage += `\n\nMissing required fields:\n- ${error.response.missing_fields.join('\n- ')}`;
      }

      // If there's a message in the response, include it too
      if (error?.response?.message && error.response.message !== error.message) {
        errorMessage += `\n\n${error.response.message}`;
      }

      alert(errorMessage);
    },
  });

  const handleReleaseFunds = () => {
    if (!selectedAccount) return;
    const amount = parseFloat(releaseAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    const availableBalance = getBalance(selectedAccount, 'available');
    if (amount > availableBalance) {
      alert(`Amount exceeds available balance ($${availableBalance.toFixed(2)})`);
      return;
    }
    if (selectedBatchId === 'new' && !newBatchName.trim()) {
      alert('Please enter a name for the new batch');
      return;
    }
    if (window.confirm(`Release $${amount.toFixed(2)} from available balance?`)) {
      releaseFundsMutation.mutate({
        accountId: selectedAccount.id,
        amount,
        batchId: selectedBatchId === 'new' ? null : selectedBatchId,
        newBatchName: selectedBatchId === 'new' ? newBatchName : undefined,
      });
    }
  };

  // --- Bulk release helpers ---

  const toggleAccountSelection = (accountId: number) => {
    setSelectedAccountIds((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  // Accounts currently checked, in the same order they appear in the grid.
  const getSelectedAccounts = (): FinancialAccountWithBalances[] =>
    (accounts || [])
      .filter((a) => a.account_type !== 'payment_gateway' && selectedAccountIds.has(a.id))
      .sort((a, b) => getBalance(b, 'available') - getBalance(a, 'available'));

  const openBulkReleaseDialog = () => {
    // Default each selected account's amount to its available balance.
    const amounts: Record<number, string> = {};
    for (const account of getSelectedAccounts()) {
      amounts[account.id] = getBalance(account, 'available').toFixed(2);
    }
    setBulkAmounts(amounts);
    setBulkResults(null);
    setSelectedBatchId(null);
    setNewBatchName('');
    setShowBulkReleaseDialog(true);
  };

  const closeBulkReleaseDialog = () => {
    setShowBulkReleaseDialog(false);
    setBulkAmounts({});
    setBulkResults(null);
    setSelectedBatchId(null);
    setNewBatchName('');
  };

  const handleBulkReleaseFunds = async () => {
    const selected = getSelectedAccounts();
    if (selected.length === 0) return;

    // Validate every amount up front so we don't release some then fail.
    const releases: Array<{ account: FinancialAccountWithBalances; amount: number }> = [];
    for (const account of selected) {
      const amount = parseFloat(bulkAmounts[account.id] ?? '');
      if (isNaN(amount) || amount <= 0) {
        alert(`Enter a valid amount for ${getEntityName(account)}`);
        return;
      }
      const available = getBalance(account, 'available');
      if (amount > available) {
        alert(`${getEntityName(account)}: amount exceeds available balance (${formatCurrency(available)})`);
        return;
      }
      releases.push({ account, amount });
    }

    if (selectedBatchId === 'new' && !newBatchName.trim()) {
      alert('Please enter a name for the new batch');
      return;
    }

    const total = releases.reduce((sum, r) => sum + r.amount, 0);
    if (!window.confirm(`Release ${formatCurrency(total)} across ${releases.length} account(s)?`)) {
      return;
    }

    setIsBulkReleasing(true);
    const errors: Array<{ name: string; error: string }> = [];
    let done = 0;
    // First release decides/creates the batch; remaining releases reuse it so
    // the whole bulk lands in a single batch.
    let resolvedBatchId: number | null = selectedBatchId === 'new' ? null : selectedBatchId;
    let createNewBatchName: string | undefined = selectedBatchId === 'new' ? newBatchName : undefined;

    for (const { account, amount } of releases) {
      try {
        const result = await financialAccountApi.releaseFunds(account.id, amount, {
          batch_id: resolvedBatchId,
          new_batch_name: createNewBatchName,
        });
        // After the first call, pin subsequent releases to the resulting batch.
        if (result?.batch_id) {
          resolvedBatchId = result.batch_id;
          createNewBatchName = undefined;
        }
        done++;
      } catch (error) {
        const err = error as { response?: { message?: string }; message?: string };
        errors.push({
          name: getEntityName(account),
          error: err?.response?.message || err?.message || String(error),
        });
      }
      setBulkResults({ done, total: releases.length, errors: [...errors] });
    }

    setIsBulkReleasing(false);
    queryClient.invalidateQueries({ queryKey: ['financialAccounts'] });
    queryClient.invalidateQueries({ queryKey: ['availableBatches'] });

    if (errors.length === 0) {
      toast({
        title: 'Funds released',
        description: `Released funds for ${done} account(s).`,
      });
      setSelectedAccountIds(new Set());
      closeBulkReleaseDialog();
    } else {
      toast({
        title: 'Bulk release finished with errors',
        description: `${done} succeeded, ${errors.length} failed. See the dialog for details.`,
        variant: 'destructive',
      });
    }
  };

  // Export all account balances to CSV
  const handleExportAllBalances = () => {
    if (!accounts || accounts.length === 0) {
      toast({
        title: 'No Data',
        description: 'No account balances to export',
        variant: 'destructive',
      });
      return;
    }

    // Filter out payment gateway accounts and prepare export data
    const exportData = accounts
      .filter(account => account.account_type !== 'payment_gateway')
      .map(account => ({
        account_id: account.id,
        account_type: account.account_type,
        reference: account.reference || '',
        display_name: getEntityName(account),
        status: account.status,
        available_balance: getBalance(account, 'available').toFixed(2),
        current_balance: getBalance(account, 'current').toFixed(2),
        pending_balance: getBalance(account, 'pending').toFixed(2),
        escrow_balance: getBalance(account, 'escrow').toFixed(2),
        chargeback_holding: account.account_type === 'management_company' ? getBalance(account, 'chargeback_holding').toFixed(2) : '',
        pending_release: getBalance(account, 'pending_release').toFixed(2),
        currency: 'USD',
      }));

    // Create CSV headers
    const headers = [
      'account_id',
      'account_type',
      'reference',
      'display_name',
      'status',
      'available_balance',
      'current_balance',
      'pending_balance',
      'escrow_balance',
      'chargeback_holding',
      'pending_release',
      'currency',
    ];

    // Build CSV content
    const csvRows = [headers.join(',')];
    exportData.forEach(row => {
      const values = headers.map(header => {
        const value = row[header as keyof typeof row];
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');
    const filename = `account_balances_export_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);

    toast({
      title: 'Export Successful',
      description: `Exported ${exportData.length} account balances to CSV`,
    });
  };

  // Export all accounts with bank details to CSV
  const handleExportAccountsWithBankDetails = () => {
    if (!accounts || accounts.length === 0) {
      toast({
        title: 'No Data',
        description: 'No accounts to export',
        variant: 'destructive',
      });
      return;
    }

    // Filter out payment gateway accounts and prepare export data
    // Each account may have multiple payout accounts, so we'll create one row per payout account
    const exportRows: Array<Record<string, string>> = [];

    accounts
      .filter(account => account.account_type !== 'payment_gateway')
      .forEach(account => {
        const baseData = {
          account_id: String(account.id),
          account_type: account.account_type,
          reference: account.reference || '',
          display_name: account.display_name || getEntityName(account),
          status: account.status,
          available_balance: getBalance(account, 'available').toFixed(2),
          current_balance: getBalance(account, 'current').toFixed(2),
          pending_balance: getBalance(account, 'pending').toFixed(2),
          pending_release: getBalance(account, 'pending_release').toFixed(2),
        };

        if (account.payout_accounts && account.payout_accounts.length > 0) {
          // Create a row for each payout account
          account.payout_accounts.forEach(payout => {
            exportRows.push({
              ...baseData,
              payout_account_id: String(payout.id),
              payout_method: payout.payout_method || '',
              is_default_payout: payout.is_default ? 'Yes' : 'No',
              account_holder_name: payout.account_holder_name || '',
              recipient_type: payout.recipient_type || '',
              bank_name: payout.bank_name || '',
              bank_account_number: payout.bank_account_number || '',
              bank_routing_number: payout.bank_routing_number || '',
              bank_swift_code: payout.bank_swift_code || '',
              bank_account_type: payout.bank_account_type || '',
              street_address: payout.street_address || '',
              city: payout.city || '',
              state_province: payout.state_province || '',
              postcode: payout.postcode || '',
              country: payout.country || '',
              transfer_purpose: payout.transfer_purpose || '',
              transfer_description: payout.transfer_description || '',
              payout_status: payout.status || '',
              verified_at: payout.verified_at || '',
            });
          });
        } else {
          // No payout accounts - still export the account with empty bank fields
          exportRows.push({
            ...baseData,
            payout_account_id: '',
            payout_method: '',
            is_default_payout: '',
            account_holder_name: '',
            recipient_type: '',
            bank_name: '',
            bank_account_number: '',
            bank_routing_number: '',
            bank_swift_code: '',
            bank_account_type: '',
            street_address: '',
            city: '',
            state_province: '',
            postcode: '',
            country: '',
            transfer_purpose: '',
            transfer_description: '',
            payout_status: '',
            verified_at: '',
          });
        }
      });

    // Create CSV headers
    const headers = [
      'account_id',
      'account_type',
      'reference',
      'display_name',
      'status',
      'available_balance',
      'current_balance',
      'pending_balance',
      'pending_release',
      'payout_account_id',
      'payout_method',
      'is_default_payout',
      'account_holder_name',
      'recipient_type',
      'bank_name',
      'bank_account_number',
      'bank_routing_number',
      'bank_swift_code',
      'bank_account_type',
      'street_address',
      'city',
      'state_province',
      'postcode',
      'country',
      'transfer_purpose',
      'transfer_description',
      'payout_status',
      'verified_at',
    ];

    // Build CSV content
    const csvRows = [headers.join(',')];
    exportRows.forEach(row => {
      const values = headers.map(header => {
        const value = row[header] || '';
        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');
    const filename = `accounts_with_bank_details_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);

    toast({
      title: 'Export Successful',
      description: `Exported ${exportRows.length} rows (accounts with bank details) to CSV`,
    });
  };

  // Handle statement export
  const handleStatementExport = async () => {
    if (!selectedStatementId) {
      toast({
        title: 'Error',
        description: 'Please select a statement',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);

    try {
      const statement = statements?.find((s) => s.id === selectedStatementId);
      const statementNumber = statement?.statement_number || 'statement';
      const accountName = exportAccountId
        ? accounts?.find((a) => a.id === exportAccountId)?.display_name ||
          getEntityName(accounts?.find((a) => a.id === exportAccountId)!)
        : undefined;

      if (exportFormat === 'csv') {
        // Get CSV content from API
        let csvContent = await financialStatementApi.exportStatementCSV(
          selectedStatementId,
          exportAccountId || undefined
        );

        // Filter out Platform account rows if unchecked (only when exporting all accounts)
        if (!includePlatform && !exportAccountId) {
          const lines = csvContent.split('\n');
          const header = lines[0];
          // "Account Type" is the 5th column (index 4) in the CSV exported by the backend
          const filteredLines = lines.slice(1).filter((line) => {
            if (!line.trim()) return true; // preserve trailing blank lines
            // Split respecting quoted fields
            const match = line.match(/(?:^|,)("(?:[^"]|"")*"|[^,]*)/g);
            if (!match || match.length < 5) return true;
            const accountTypeField = match[4].replace(/^,/, '').replace(/^"|"$/g, '');
            return accountTypeField !== 'platform';
          });
          csvContent = [header, ...filteredLines].join('\n');
        }

        downloadStatementCSV(csvContent, statementNumber, accountName);
        toast({
          title: 'Export Successful',
          description: `Statement exported as CSV`,
        });
      } else {
        // Get data for PDF generation
        let exportData = await financialStatementApi.getStatementExportData(
          selectedStatementId,
          exportAccountId || undefined
        );

        // Filter out Platform accounts if checkbox is unchecked
        if (!includePlatform && !exportAccountId) {
          const nonPlatformAccounts = exportData.accounts.filter(
            (a) => a.account_type !== 'platform'
          );
          const nonPlatformIds = new Set(nonPlatformAccounts.map((a) => a.id));
          exportData = {
            ...exportData,
            accounts: nonPlatformAccounts,
            snapshots: exportData.snapshots.filter((s) => nonPlatformIds.has(s.account_id)),
          };
        }

        // Filter out accounts with no transactions if checkbox is unchecked
        if (!includeEmptyAccounts && !exportAccountId) {
          // Filter accounts that have activity in this period
          const filteredAccounts = exportData.accounts.filter((account) => {
            // Check if this account has any transactions in this period
            const hasTransactions = exportData.transactions.some(
              (t) => t.account_from_id === account.id || t.account_to_id === account.id
            );
            if (hasTransactions) return true;

            // Check if this account has any credits or debits in the snapshot
            const snapshot = exportData.snapshots.find((s) => s.account_id === account.id);
            if (snapshot) {
              const credits = Number(snapshot.total_credits) || 0;
              const debits = Number(snapshot.total_debits) || 0;
              if (credits > 0 || debits > 0) return true;
            }

            return false;
          });

          // Get IDs of accounts to keep
          const accountIdsToKeep = new Set(filteredAccounts.map((a) => a.id));

          // Filter snapshots to match filtered accounts
          exportData = {
            ...exportData,
            accounts: filteredAccounts,
            snapshots: exportData.snapshots.filter((s) => accountIdsToKeep.has(s.account_id)),
          };
        }

        if (exportAccountId) {
          // Export single account PDF
          await downloadStatementPDF(exportData, exportAccountId);
          toast({
            title: 'Export Successful',
            description: `Statement exported as PDF`,
          });
        } else {
          // Export all accounts - generate single combined PDF
          await downloadAllAccountsStatementPDF(exportData);
          toast({
            title: 'Export Successful',
            description: `Combined statement PDF exported for ${exportData.accounts.length} accounts`,
          });
        }
      }

      setShowStatementExportDialog(false);
      setSelectedStatementId(null);
      setExportAccountId(null);
      setIncludeEmptyAccounts(true);
      setIncludePlatform(true);
    } catch (error) {
      console.error('Statement export error:', error);
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Failed to export statement',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Open statement export dialog for a specific account
  const openAccountStatementExport = (accountId: number) => {
    setExportAccountId(accountId);
    setShowStatementExportDialog(true);
  };

  // Format statement month for display
  const formatStatementMonth = (monthStr: string): string => {
    // Parse the date string as local time to avoid timezone issues
    // monthStr is in format "2025-12-01"
    const [year, month] = monthStr.split('-').map(Number);
    const date = new Date(year, month - 1, 1); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  };

  // Helper to get entity name
  const getEntityName = (account: FinancialAccountWithBalances): string => {
    if (account.account_type === 'platform') return 'Platform';
    if (account.account_type === 'payment_gateway') return `Gateway: ${account.reference}`;
    return `${account.account_type} #${account.reference}`;
  };

  // Helper to get balance by type (USD only)
  const getBalance = (account: FinancialAccountWithBalances, balanceType: BalanceType): number => {
    const balance = account.balances?.find(b => b.balance_type === balanceType && b.currency === 'USD');
    return balance?.amount || 0;
  };

  // Helper to format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">Financial Accounts</h1>
          <p className="text-gray-600">Manage financial accounts and balances</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExportAllBalances}
            disabled={isLoading || !accounts || accounts.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400"
          >
            Export All Balances
          </button>
          <button
            onClick={handleExportAccountsWithBankDetails}
            disabled={isLoading || !accounts || accounts.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-400"
          >
            Export with Bank Details
          </button>
          <button
            onClick={() => {
              setExportAccountId(null);
              setShowStatementExportDialog(true);
            }}
            disabled={!statements || statements.length === 0}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-400"
          >
            Export Statement
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Type
            </label>
            <select
              value={selectedAccountType}
              onChange={(e) => setSelectedAccountType(e.target.value as AccountType | '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="platform">Platform</option>
              <option value="association">Association</option>
              <option value="management_company">Management Company</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          Error loading accounts: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {/* Bulk action bar — shown when at least one account is selected */}
      {!isLoading && !error && accounts && selectedAccountIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <span className="text-sm font-medium text-blue-900">
            {selectedAccountIds.size} account{selectedAccountIds.size === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedAccountIds(new Set())}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear selection
            </button>
            <button
              onClick={openBulkReleaseDialog}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Release Funds
            </button>
          </div>
        </div>
      )}

      {/* Accounts List */}
      {!isLoading && !error && accounts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {accounts
            .filter(account => account.account_type !== 'payment_gateway')
            .sort((a, b) => getBalance(b, 'available') - getBalance(a, 'available'))
            .map((account) => (
            <div
              key={account.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedAccount(account)}
            >
              {/* Account Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-3">
                  {/* Selection checkbox — only for accounts with available funds */}
                  {getBalance(account, 'available') > 0 && (
                    <input
                      type="checkbox"
                      checked={selectedAccountIds.has(account.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleAccountSelection(account.id);
                      }}
                      aria-label={`Select ${getEntityName(account)} for bulk release`}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  )}
                  <div>
                    <h3 className="text-xl font-semibold">{getEntityName(account)}</h3>
                    <p className="text-sm text-gray-500">ID: {account.id}</p>
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    account.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : account.status === 'suspended'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {account.status}
                </span>
              </div>

              {/* Balances */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Available:</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(getBalance(account, 'available'))}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Current Balance:</span>
                  <span className="font-semibold text-lg">
                    {formatCurrency(getBalance(account, 'current'))}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pending:</span>
                  <span className="font-medium text-blue-600">
                    {formatCurrency(getBalance(account, 'pending'))}
                  </span>
                </div>
                {getBalance(account, 'escrow') > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Escrow:</span>
                    <span className="font-medium text-yellow-600">
                      {formatCurrency(getBalance(account, 'escrow'))}
                    </span>
                  </div>
                )}
                {account.account_type === 'management_company' &&
                  getBalance(account, 'chargeback_holding') > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Chargeback Holding:</span>
                      <span className="font-medium text-orange-600">
                        {formatCurrency(getBalance(account, 'chargeback_holding'))}
                      </span>
                    </div>
                  )}
              </div>

              {/* Payout Accounts */}
              {account.payout_accounts && account.payout_accounts.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Payout Methods:</p>
                  {account.payout_accounts.map((payout) => (
                    <div key={payout.id} className="flex items-center text-sm">
                      <span
                        className={`inline-block w-2 h-2 rounded-full mr-2 ${
                          payout.is_default ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      ></span>
                      <span className="text-gray-700">
                        {payout.payout_method === 'bank_account'
                          ? `${payout.bank_name} ****${payout.bank_account_number?.slice(-4)}`
                          : payout.token_provider}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Account Actions */}
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                {/* Export Statement Button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openAccountStatementExport(account.id);
                  }}
                  type="button"
                  className="w-full px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 z-10 relative"
                >
                  Export Statement
                </button>

                {/* Release Funds Button */}
                {getBalance(account, 'available') > 0 && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('[FinancialAccounts] Release funds button clicked', {
                        accountId: account.id,
                        accountName: getEntityName(account),
                        availableBalance: getBalance(account, 'available'),
                        account: account
                      });
                      setSelectedAccount(account);
                      setShowReleaseFundsDialog(true);
                      console.log('[FinancialAccounts] Dialog state set to true', {
                        showReleaseFundsDialog: true,
                        selectedAccount: account
                      });
                    }}
                    type="button"
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 z-10 relative"
                  >
                    Release Funds ({formatCurrency(getBalance(account, 'available'))} available)
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && accounts && accounts.filter(account => account.account_type !== 'payment_gateway').length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No financial accounts found</p>
        </div>
      )}

      {/* Account Detail Modal (simplified) */}
      {selectedAccount && !showReleaseFundsDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedAccount(null)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold">{getEntityName(selectedAccount)}</h2>
              <button
                onClick={() => setSelectedAccount(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Account Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Account ID:</span>
                    <p className="font-medium">{selectedAccount.id}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <p className="font-medium">{selectedAccount.account_type}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <p className="font-medium">{selectedAccount.status}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Reference:</span>
                    <p className="font-medium">{selectedAccount.reference || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">All Balances</h3>
                <div className="space-y-2">
                  {selectedAccount.balances?.map((balance) => (
                    <div
                      key={balance.id}
                      className="flex justify-between items-center p-2 bg-gray-50 rounded"
                    >
                      <span className="text-sm capitalize">{balance.balance_type}:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{formatCurrency(balance.amount)}</span>
                        {balance.balance_type === 'available' && balance.amount > 0 && (
                          <button
                            onClick={() => setShowReleaseFundsDialog(true)}
                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Release Funds
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Release Funds Dialog */}
      {(() => {
        console.log('[FinancialAccounts] Render check:', {
          showReleaseFundsDialog,
          hasSelectedAccount: !!selectedAccount,
          selectedAccountId: selectedAccount?.id
        });
        return null;
      })()}
      {showReleaseFundsDialog && selectedAccount && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => {
            console.log('[FinancialAccounts] Backdrop clicked, closing dialog');
            setShowReleaseFundsDialog(false);
            setReleaseAmount('');
            setSelectedBatchId(null);
            setNewBatchName('');
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Release Funds</h2>
              <button
                onClick={() => {
                  setShowReleaseFundsDialog(false);
                  setReleaseAmount('');
                  setSelectedBatchId(null);
                  setNewBatchName('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Account: <span className="font-medium">{getEntityName(selectedAccount)}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Available Balance:{' '}
                  <span className="font-medium text-green-600">
                    {formatCurrency(getBalance(selectedAccount, 'available'))}
                  </span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount to Release
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={getBalance(selectedAccount, 'available')}
                  value={releaseAmount}
                  onChange={(e) => setReleaseAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add to Batch
                </label>
                <select
                  value={selectedBatchId === null ? '' : selectedBatchId}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'new') {
                      setSelectedBatchId('new');
                    } else if (value === '') {
                      setSelectedBatchId(null);
                    } else {
                      setSelectedBatchId(parseInt(value));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Auto-select (today's batch)</option>
                  <option value="new">+ Create New Batch</option>
                  {availableBatches && availableBatches.length > 0 && (
                    <>
                      <option disabled>──────────────</option>
                      {availableBatches.map((batch) => (
                        <option key={batch.id} value={batch.id}>
                          {batch.batch_number} - ${Number(batch.total_amount).toFixed(2)} ({batch.total_payouts} payouts)
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select an existing batch or create a new one
                </p>
              </div>

              {selectedBatchId === 'new' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Batch Name
                  </label>
                  <input
                    type="text"
                    value={newBatchName}
                    onChange={(e) => setNewBatchName(e.target.value)}
                    placeholder="e.g., January 2026 Payouts"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              <div className="bg-blue-50 p-3 rounded text-sm text-gray-700">
                <p>
                  Releasing funds will move the specified amount from{' '}
                  <strong>available</strong> to <strong>pending_release</strong> and add it to the selected batch.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowReleaseFundsDialog(false);
                    setReleaseAmount('');
                    setSelectedBatchId(null);
                    setNewBatchName('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReleaseFunds}
                  disabled={releaseFundsMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {releaseFundsMutation.isPending ? 'Releasing...' : 'Release Funds'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Release Funds Dialog */}
      {showBulkReleaseDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => { if (!isBulkReleasing) closeBulkReleaseDialog(); }}
        >
          <div
            className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-start p-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold">Release Funds</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {getSelectedAccounts().length} account{getSelectedAccounts().length === 1 ? '' : 's'} selected
                </p>
              </div>
              <button
                onClick={closeBulkReleaseDialog}
                disabled={isBulkReleasing}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                ✕
              </button>
            </div>

            {/* Scrollable account list */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {getSelectedAccounts().map((account) => {
                const available = getBalance(account, 'available');
                const result = bulkResults?.errors.find((er) => er.name === getEntityName(account));
                return (
                  <div key={account.id} className="flex items-center justify-between gap-4 border border-gray-200 rounded-md p-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{getEntityName(account)}</p>
                      <p className="text-xs text-gray-500">
                        Available: <span className="text-green-600 font-medium">{formatCurrency(available)}</span>
                      </p>
                      {result && (
                        <p className="text-xs text-red-600 mt-1">Failed: {result.error}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={available}
                        value={bulkAmounts[account.id] ?? ''}
                        disabled={isBulkReleasing}
                        onChange={(e) =>
                          setBulkAmounts((prev) => ({ ...prev, [account.id]: e.target.value }))
                        }
                        className="w-32 px-3 py-2 border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer: batch selector + actions */}
            <div className="border-t border-gray-200 p-6 pt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add to Batch
                </label>
                <select
                  value={selectedBatchId === null ? '' : selectedBatchId}
                  disabled={isBulkReleasing}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'new') setSelectedBatchId('new');
                    else if (value === '') setSelectedBatchId(null);
                    else setSelectedBatchId(parseInt(value));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Auto-select (today's batch)</option>
                  <option value="new">+ Create New Batch</option>
                  {availableBatches && availableBatches.length > 0 && (
                    <>
                      <option disabled>──────────────</option>
                      {availableBatches.map((batch) => (
                        <option key={batch.id} value={batch.id}>
                          {batch.batch_number} - ${Number(batch.total_amount).toFixed(2)} ({batch.total_payouts} payouts)
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <p className="mt-1 text-xs text-gray-500">All selected releases are added to the same batch.</p>
              </div>

              {selectedBatchId === 'new' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Batch Name
                  </label>
                  <input
                    type="text"
                    value={newBatchName}
                    disabled={isBulkReleasing}
                    onChange={(e) => setNewBatchName(e.target.value)}
                    placeholder="e.g., January 2026 Payouts"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              )}

              {bulkResults && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700">
                  Processed {bulkResults.done} / {bulkResults.total}
                  {bulkResults.errors.length > 0 && (
                    <span className="text-red-600"> · {bulkResults.errors.length} failed</span>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={closeBulkReleaseDialog}
                  disabled={isBulkReleasing}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  {bulkResults && bulkResults.errors.length > 0 ? 'Close' : 'Cancel'}
                </button>
                <button
                  onClick={handleBulkReleaseFunds}
                  disabled={isBulkReleasing}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {isBulkReleasing
                    ? `Releasing... (${bulkResults?.done ?? 0}/${getSelectedAccounts().length})`
                    : 'Release Funds'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statement Export Dialog */}
      {showStatementExportDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => {
            setShowStatementExportDialog(false);
            setSelectedStatementId(null);
            setExportAccountId(null);
            setIncludeEmptyAccounts(true);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Export Statement</h2>
              <button
                onClick={() => {
                  setShowStatementExportDialog(false);
                  setSelectedStatementId(null);
                  setExportAccountId(null);
                  setIncludeEmptyAccounts(true);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Account selection (if opened from header button) */}
              {exportAccountId === null && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account (Optional)
                  </label>
                  <select
                    value={exportAccountId || ''}
                    onChange={(e) =>
                      setExportAccountId(e.target.value ? parseInt(e.target.value) : null)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">All Accounts</option>
                    {accounts
                      ?.filter((a) => a.account_type !== 'payment_gateway')
                      .map((account) => (
                        <option key={account.id} value={account.id}>
                          {getEntityName(account)}
                        </option>
                      ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Leave empty to export all accounts
                  </p>
                </div>
              )}

              {/* Selected account (if opened from account card) */}
              {exportAccountId !== null && (
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">
                    Exporting for:{' '}
                    <span className="font-medium">
                      {getEntityName(accounts?.find((a) => a.id === exportAccountId)!)}
                    </span>
                  </p>
                </div>
              )}

              {/* Statement selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statement Period
                </label>
                <select
                  value={selectedStatementId || ''}
                  onChange={(e) =>
                    setSelectedStatementId(e.target.value ? parseInt(e.target.value) : null)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select a statement...</option>
                  {statements?.map((statement) => (
                    <option key={statement.id} value={statement.id}>
                      {formatStatementMonth(statement.statement_month)} ({statement.statement_number})
                      {statement.status === 'open' ? ' - Current' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Format selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Format
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={() => setExportFormat('csv')}
                      className="mr-2 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm">CSV</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="pdf"
                      checked={exportFormat === 'pdf'}
                      onChange={() => setExportFormat('pdf')}
                      className="mr-2 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm">PDF (Bank Statement)</span>
                  </label>
                </div>
              </div>

              {/* Include empty accounts checkbox - only show when exporting all accounts */}
              {exportAccountId === null && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includeEmptyAccounts}
                      onChange={(e) => setIncludeEmptyAccounts(e.target.checked)}
                      className="mr-2 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Include accounts with no transactions</span>
                  </label>
                </div>
              )}

              {/* Include Platform checkbox - only show when exporting all accounts */}
              {exportAccountId === null && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includePlatform}
                      onChange={(e) => setIncludePlatform(e.target.checked)}
                      className="mr-2 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Include Platform account</span>
                  </label>
                </div>
              )}

              {/* Info box */}
              <div className="bg-orange-50 p-3 rounded text-sm text-gray-700">
                <p>
                  {exportFormat === 'csv' ? (
                    <>
                      The CSV export includes starting balance, all transactions (parent and
                      children), and ending balance for each account.
                    </>
                  ) : (
                    <>
                      The PDF export generates a professional bank-style statement with logo,
                      statement period, balance summary, and transaction details.
                      {!includeEmptyAccounts && exportAccountId === null && (
                        <> Accounts with no transactions will be excluded.</>
                      )}
                    </>
                  )}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowStatementExportDialog(false);
                    setSelectedStatementId(null);
                    setExportAccountId(null);
                    setIncludeEmptyAccounts(true);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatementExport}
                  disabled={!selectedStatementId || isExporting}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-400"
                >
                  {isExporting ? 'Exporting...' : 'Export'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialAccounts;
